import { MINOR_APIs } from "libs/apis/minor.api";
import { useNavigate } from "react-router-dom";
import { successToaster } from "utils/helpers/common/alert-service";
import { MinorDTO } from "utils/helpers/models/attendee/minor.dto";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { PictureDTO } from "utils/helpers/models/shared/picture.dto";
import useForms from "hooks/useForms";
import useStore from "hooks/useStore";
import useAuth from "containers/auth/useHooks";
import { set } from "date-fns";
import { siteRoutes } from "utils/helpers/enums/routes";

const useMinor = () => {
  const navigate = useNavigate();
  const { getUserId, updateProfileCompletionStatus } = useStore();
  const { getUserProfile } = useAuth();
  const { patchFormValues } = useForms();

  const getMinorProfile = async (setMinorProfileData: Function) => {
    const response = await MINOR_APIs.getMinorProfile();
    const { status = false, data } = response || {};
    if (status) {
      setMinorProfileData({
        ...data.user,
        ...data.profiles.child,
      });
    }
  };

  const getMinorHome = async (
    setRecommendedEvents: Function,
    setUpcomingEvents: Function
  ) => {
    const response = await MINOR_APIs.getMinorHome();
    const { status = false } = response || {};
    if (status) {
      setRecommendedEvents(response.data.recommendedEvents);
      setUpcomingEvents(response.data.upcomingEvents);
    }
  };

  const getEvents = async (
    setData: Function,
    queryParams: any = {},
    previousData: EventDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await MINOR_APIs.getEventsTestingRefactored(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages, totalElements } = data;
      if (setFilters)
        setFilters((filters) => ({ ...filters, totalPages, totalElements }));
      const events = data.content.map((i) => new EventDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  const getEventById = async (
    id: number,
    setFormData: Function,
    setValue?: Function
  ) => {
    const response = await MINOR_APIs.getEventById(id);
    const { status = false, data = null } = response || {};
    if (status) {
      const { event, host, rsvp = {} } = data;
      const model = new EventDTO({ ...event, host, rsvp });
      if (setValue) {
        patchFormValues(model, setFormData, setValue);
      } else {
        setFormData({ ...model });
      }
    }
  };

  const updateMinor = async (minorId: number, body: Partial<MinorDTO>) => {
    const response = await MINOR_APIs.updateMinor(minorId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);

      // Refresh user profile data in localStorage
      getUserProfile();

      // Update profileCompleted to true in localStorage
      updateProfileCompletionStatus("minor");

      navigate(siteRoutes.minorHome);
    }
  };

  const getAttendeeHome = async (setData: Function) => {
    const response = await MINOR_APIs.getAttendeeHome();
    const { status = false, data = {} } = response || {};
    if (status) {
      const { recommendedEvents = [] } = data;
      setData(recommendedEvents.map((i) => new EventDTO(i)));
    }
  };

  const getUserById = async (userId: number, setData: Function) => {
    const response = await MINOR_APIs.getUserById(userId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const addToWishlist = async (eventId: number) => {
    const response = await MINOR_APIs.addToWishlist(eventId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getWishlistedMinors = async (eventId: number, setData: Function) => {
    const response = await MINOR_APIs.getWishlistedMinors(eventId);
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(data.map((i) => new MinorDTO(i)));
    }
  };

  const removeFromWishlist = async (eventId: number) => {
    const response = await MINOR_APIs.removeFromWishlist(eventId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getMyBadges = async (
    setData: Function,
    queryParams: any,
    setTotalElements?: Function
  ) => {
    const response = await MINOR_APIs.getMyBadges(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { badges, totalElements = 0 } = data;
      setData(badges);
      setTotalElements?.(totalElements);
    }
  };

  const toggleNotification = async () => {
    const response = await MINOR_APIs.toggleNotification();
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const uploadFile = async (file: File): Promise<PictureDTO | null> => {
    const body = new FormData();
    body.append("file", file);
    const response = await MINOR_APIs.uploadFile(body);
    const { status = false, data } = response || {};
    if (status) {
      return new PictureDTO(data);
    } else {
      return null;
    }
  };



  return {
    getMinorProfile,
    getEvents,
    getEventById,
    getMinorHome,
    getAttendeeHome,
    getUserById,
    removeFromWishlist,
    addToWishlist,
    getWishlistedMinors,
    getMyBadges,
    uploadFile,
    updateMinor,
    toggleNotification,
  };
};

export default useMinor;
